import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { LogOut, MessageCircle, Sun, Moon } from "lucide-react";
import { Auth } from "aws-amplify";
import { useIsMobile, MobileBottomNav } from "@hotelise/ui";
import { useDispatch, useSelector } from "react-redux";

import PropertiesPage from "components/pages/PropertiesPage";
import PropertyDetailsPage from "components/pages/PropertyDetailsPage";
import StatementsPage from "components/pages/StatementsPage";
import Dashboard from "components/pages/Dashboard";
import ReservationsPage from "components/pages/ReservationsPage";
import ExpensesPage from "components/pages/ExpensesPage";
import CalendarPage from "components/pages/CalendarPage";
import GuestsPage from "components/pages/GuestsPage";
import AgreementsPage from "components/pages/AgreementsPage";
import ServicePropertyDetailsPage from "components/pages/ServicePropertyDetailsPage";

import { AppFlowContainer } from "components/atoms";

import { Navbar, Topbar } from "components/molecules";

import { SIZES } from "styles/size";
import { getUser, getUserData } from "state/modules/user";
import { useTrengoSync } from "hooks/useTrengoSync";

import { navRoutes } from "utils/routes";

const getInitialSidebarState = (): boolean => {
	const stored = localStorage.getItem("sidebar:state");
	if (stored !== null) return stored === "true";
	return true;
};

const getInitialDarkMode = (): boolean => {
	const stored = localStorage.getItem("theme");
	if (stored) return stored === "dark";
	return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const PRIMARY_NAV_ROUTE_IDS = ["/", "/properties", "/reservations", "/calendar", "/guests", "/expenses"];
const MORE_NAV_ROUTE_IDS = ["/statements", "/services"];

const primaryNavConfig = navRoutes
	.filter((r) => PRIMARY_NAV_ROUTE_IDS.includes(r.to))
	.map((r) => ({
		id: r.to,
		icon: React.createElement(r.icon!),
		label: r.title,
	}));

const moreNavConfig = navRoutes
	.filter((r) => MORE_NAV_ROUTE_IDS.includes(r.to))
	.map((r) => ({
		id: r.to,
		icon: React.createElement(r.icon!),
		label: r.title,
	}));

const routeElements = (
	<Routes>
		<Route index element={<Dashboard />} />
		<Route path="properties" element={<PropertiesPage />} />
		<Route path="properties/:id" element={<PropertyDetailsPage />} />
		<Route path="statements" element={<StatementsPage />} />
		<Route path="reservations" element={<ReservationsPage />} />
		<Route path="calendar" element={<CalendarPage />} />
		<Route path="guests" element={<GuestsPage viewMode="owner" />} />
		<Route path="expenses" element={<ExpensesPage />} />
		<Route path="services/agreements" element={<AgreementsPage />} />
		<Route path="services/property-details" element={<ServicePropertyDetailsPage />} />
		<Route path="services" element={<Navigate to="/services/agreements" replace />} />
		<Route path="*" element={<Navigate to="/" replace />} />
	</Routes>
);

const AppFlow = (): JSX.Element => {
	const [isNavbarExpanded, setNavbarExpanded] = useState(
		getInitialSidebarState
	);
	const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const location = useLocation();

	const dispatch = useDispatch();

	const user = useSelector(getUserData);

	useEffect(() => {
		dispatch(getUser());
	}, [dispatch]);

	useTrengoSync(user);

	useEffect(() => {
		document.documentElement.classList.toggle("dark", isDarkMode);
		localStorage.setItem("theme", isDarkMode ? "dark" : "light");
	}, [isDarkMode]);

	const toggleTheme = useCallback(() => {
		setIsDarkMode((prev) => !prev);
	}, []);

	// Close Trengo widget when resizing from mobile to desktop
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 768) {
				const w = window as any;
				if (w.Trengo?.eventBus) {
					w.Trengo.eventBus.$emit("widget.close");
				}
			}
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const toggleNavbar = useCallback(() => {
		setNavbarExpanded((prev) => {
			const next = !prev;
			localStorage.setItem("sidebar:state", String(next));
			return next;
		});
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "b" && (event.metaKey || event.ctrlKey)) {
				event.preventDefault();
				toggleNavbar();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [toggleNavbar]);

	const handleChatClick = () => {
		const w = window as any;
		if (w.Trengo?.eventBus) {
			const isWidgetOpen = Boolean(
				document.querySelector("iframe.trengo-vue-iframe.visible")
			);
			w.Trengo.eventBus.$emit(
				isWidgetOpen ? "widget.close" : "widget.open"
			);
		}
	};

	const withNav = (
		config: { id: string; icon: React.ReactNode; label: string }[]
	) => config.map((item) => ({ ...item, onClick: () => navigate(item.id) }));

	const activeNavId = navRoutes.find((r) => {
		if (r.to === "/") return location.pathname === "/";
		if (r.children) return location.pathname.startsWith(r.to) || r.children.some((c) => location.pathname.startsWith(c.to));
		return location.pathname.startsWith(r.to);
	})?.to;

	if (!user) {
		return <AppFlowContainer>{null as any}</AppFlowContainer>;
	}

	if (isMobile) {
		return (
			<AppFlowContainer>
				<div className="flex flex-col min-h-svh w-full overflow-x-hidden">
					<header className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b border-border">
						<div className="h-14 px-3 flex items-center justify-between gap-2">
							<span className="font-semibold text-foreground">
								Hotelise
							</span>
							<button
								onClick={toggleTheme}
								title="Toggle Theme"
								className="w-7 h-7 flex items-center justify-center bg-transparent border-none rounded-md outline-none cursor-pointer text-foreground transition-colors duration-150 hover:bg-accent"
							>
								{isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
							</button>
						</div>
					</header>
					<main className="flex-1 px-3 py-4 pb-20 overflow-y-auto">
						{routeElements}
					</main>
					<MobileBottomNav
						items={withNav(primaryNavConfig)}
						moreItems={withNav(moreNavConfig)}
						activeId={activeNavId}
						moreFooter={
							<div className="flex flex-col gap-2">
								<button
									type="button"
									onClick={handleChatClick}
									className="flex items-center gap-3 w-full p-3 rounded-lg text-foreground hover:bg-accent transition-colors"
								>
									<MessageCircle className="h-5 w-5" />
									<span className="text-sm font-medium">
										Chat
									</span>
								</button>
								<button
									type="button"
									onClick={async () => { try { await Auth.signOut(); } catch { /* ignore */ } window.location.href = '/'; }}
									className="flex items-center gap-3 w-full p-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
								>
									<LogOut className="h-5 w-5" />
									<span className="text-sm font-medium">
										Logout
									</span>
								</button>
							</div>
						}
					/>
				</div>
			</AppFlowContainer>
		);
	}

	return (
		<AppFlowContainer>
			<div className="flex h-screen w-full overflow-hidden">
				<div
					className="shrink-0"
					style={{
						width: isNavbarExpanded
							? `${SIZES.EXPANDED_NAVBAR_WIDTH}px`
							: `${SIZES.NAVBAR_WIDTH}px`,
					}}
				>
					<Navbar isExpanded={isNavbarExpanded} />
				</div>
				<div className="flex-1 flex flex-col h-full overflow-hidden">
					<Topbar
						toggle={toggleNavbar}
						isExpanded={isNavbarExpanded}
						isDarkMode={isDarkMode}
						toggleTheme={toggleTheme}
					/>
					<div className="overflow-y-auto px-[30px] py-5">
						{routeElements}
					</div>
				</div>
			</div>
		</AppFlowContainer>
	);
};

export default AppFlow;
